import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EditOutlined, SaveOutlined } from "@mui/icons-material";
import { Percent } from "lucide-react";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { editProductCommissionRateFromBilling } from "../../../redux/slice/order/OrderSlice";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import CustomCheckbox from "../../common/CustomCheckBox";
import CustomTextField from "../../common/CustomTextField";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { calculateCommissionAmount, calculateCommissionRate, FieldLabel, getV3CommissionTypeDisplayLabel, isV3CommissionType, sortNames } from "../../../utils/Utility";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { useModalStyles } from "../../common/useModalStyles";
import { AgencyInfo, fetchAgenciesList, useCommonReducer } from "../../../redux/slice/commonSlice";

interface EditCommissionProps {
  open: boolean;
  handleClose: () => void;
  onAddInvSpecificCommission?: (commission: any) => void;
  agencyData?: any;
  mode?: "billing" | "commission-details";
  uiVariant?: "billing" | "commission-details";
  showAmountField?: boolean;
  showReasonField?: boolean;
  disableRateField?: boolean;
  disableAmountField?: boolean;
  paymentData?: {
    paymentId: number;
    invoiceAgencyId: number;
    currentRate: number;
  } | null;
  onCommissionRateUpdate?: (data: any) => void;
  zIndex?: number;
}

const EditCommissionPopup: React.FC<EditCommissionProps> = ({
  open,
  handleClose,
  onAddInvSpecificCommission,
  agencyData,
  mode = "billing",
  uiVariant,
  showAmountField,
  showReasonField,
  disableRateField = false,
  disableAmountField = false,
  paymentData,
  onCommissionRateUpdate,
  zIndex,
}) => {
  const fieldsVariant = uiVariant ?? mode;
  const shouldShowAmount = showAmountField ?? fieldsVariant === "billing";
  const shouldShowReason = showReasonField ?? fieldsVariant === "commission-details";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const v3EntityLabel = useMemo(
    () => getV3CommissionTypeDisplayLabel(allCodeTablesValuesMap),
    [allCodeTablesValuesMap]
  );

  const { control, handleSubmit, reset, setValue, getValues, watch, formState: { errors } } =
    useForm<any>();
  const dispatch = useAppDispatch();
  const { agencyInfoList } = useCommonReducer();
  const selectedAgencyId = watch("agencyId");
  const isAgencyTypeRow =
    String(agencyData?.agencyType ?? "").trim().toUpperCase() === "AGENCY";
  const canEditAgency = mode === "billing" && isAgencyTypeRow;
  const originalAgencyId = agencyData?.originalAgencyId ?? agencyData?.agencyId;
  const hasSelectedAgency = selectedAgencyId != null && selectedAgencyId !== "";
  const isAgencyChanged =
    canEditAgency &&
    originalAgencyId != null &&
    (!hasSelectedAgency || Number(selectedAgencyId) !== Number(originalAgencyId));
  const [commissionChangeForAllFutureInvoices, setCommissionChangeForAllFutureInvoices] =
    useState(false);
  const [showOptionsPopup, setShowOptionsPopup] = useState<OptionPopupObjectProps>({
    message: "",
    show: false,
  });
  const pendingFormDataRef = useRef<any>(null);
  const confirmedAgencyRef = useRef<{ agencyId: any; agencyName: string }>({
    agencyId: agencyData?.agencyId,
    agencyName: agencyData?.agencyName || "",
  });
  const pendingAgencyRef = useRef<{ agencyId: any; agencyName: string } | null>(null);

  const reasonValue = watch("reason", "");

  const clearFutureInvoiceRateChange = useCallback(() => {
    setCommissionChangeForAllFutureInvoices(false);
    setValue("commissionChangeForAllFutureInvoices", false);
  }, [setValue]);

  const handleClick = () => {
    if (isAgencyChanged) {
      return;
    }
    const newValue = !commissionChangeForAllFutureInvoices;
    setCommissionChangeForAllFutureInvoices(newValue);
    setValue("commissionChangeForAllFutureInvoices", newValue);
  };

  useEffect(() => {
    if (!open) return;
    if (mode === "commission-details" && paymentData) {
      setValue("agencyName", agencyData?.agencyName);
      setValue("commissionRate", paymentData.currentRate);
      setValue("invoiceAmt", agencyData?.invoiceAmt);
      setValue("invoiceNumber", agencyData?.invoiceNumber ?? "");
      setValue("orderNumber", agencyData?.orderNumber ?? "");
      setValue("reason", "");
      setCommissionChangeForAllFutureInvoices(false);
      if (shouldShowAmount) {
        setValue(
          "commissionAmt",
          calculateCommissionAmount(Number(agencyData?.invoiceAmt), Number(paymentData.currentRate))
        );
      }
    } else {
      setValue("agencyId", agencyData?.agencyId ?? "");
      setValue("agencyName", agencyData?.agencyName);
      setValue("commissionRate", agencyData?.commissionRate);
      setValue("invoiceAmt", agencyData?.invoiceAmt);
      setValue("invoiceNumber", agencyData?.invoiceNumber ?? "");
      setValue("orderNumber", agencyData?.orderNumber ?? "");
      const initialCommissionAmt = calculateCommissionAmount(
        Number(agencyData?.invoiceAmt),
        Number(agencyData?.commissionRate)
      );
      setValue("commissionAmt", agencyData?.commissionAmount || initialCommissionAmt);
      setValue("commissionChangeForAllFutureInvoices", agencyData?.commissionChangeForAllFutureInvoices || false);
      setCommissionChangeForAllFutureInvoices(agencyData?.commissionChangeForAllFutureInvoices || false);
      confirmedAgencyRef.current = {
        agencyId: agencyData?.agencyId,
        agencyName: agencyData?.agencyName || "",
      };
      pendingAgencyRef.current = null;
    }
  }, [open, agencyData, mode, paymentData, setValue]);

  useEffect(() => {
    if (!open) {
      reset();
      setCommissionChangeForAllFutureInvoices(false);
      pendingFormDataRef.current = null;
      pendingAgencyRef.current = null;
      setShowOptionsPopup({ message: "", show: false });
    }
  }, [open, reset]);

  useEffect(() => {
    if (open && canEditAgency) {
      dispatch(fetchAgenciesList());
    }
  }, [open, canEditAgency, dispatch]);

  const agencyOptions = useMemo(() => {
    const options: { label: string; value: string | number }[] = [];
    const list = Array.isArray(agencyInfoList) ? agencyInfoList : [];
    const sorted = list.length > 1 ? sortNames(list, "agencyName") : list;
    sorted.forEach((agency: AgencyInfo) => {
      if (agency?.agencyId == null) return;
      options.push({
        label: agency.agencyName,
        value: agency.agencyId,
      });
    });
    if (
      agencyData?.agencyId != null &&
      !options.some((option) => Number(option.value) === Number(agencyData.agencyId))
    ) {
      options.unshift({
        label: agencyData.agencyName,
        value: agencyData.agencyId,
      });
    }
    return options;
  }, [agencyInfoList, agencyData?.agencyId, agencyData?.agencyName]);

  const selectedAgencyName = useMemo(() => {
    const match = agencyOptions.find(
      (option) => Number(option.value) === Number(selectedAgencyId)
    );
    return match?.label || agencyData?.agencyName || "";
  }, [agencyOptions, selectedAgencyId, agencyData?.agencyName]);

  const resolveSelectedAgency = (data: any) => {
    const agencyId = data?.agencyId ?? agencyData?.agencyId;
    const match = agencyOptions.find((option) => Number(option.value) === Number(agencyId));
    return {
      agencyId: agencyId != null && agencyId !== "" ? Number(agencyId) : agencyData?.agencyId,
      agencyName: match?.label || agencyData?.agencyName,
    };
  };

  const applyBillingUpdate = useCallback(
    (data: any) => {
      const selectedAgency = resolveSelectedAgency(data);
      const applyFutureRateChange =
        commissionChangeForAllFutureInvoices && !isAgencyChanged;
      if (agencyData && agencyData?.productCommissionId && applyFutureRateChange && onAddInvSpecificCommission) {
        const editData = {
          productCommissionId: agencyData?.productCommissionId,
          commissionRate: Number(data.commissionRate),
        };
        dispatch(editProductCommissionRateFromBilling(editData));
        const invSpecificCommissionData = {
          agencyId: selectedAgency.agencyId,
          invoiceAgencyId: agencyData?.invoiceAgencyId,
          agencyName: selectedAgency.agencyName,
          commissionRate: Number(data.commissionRate),
          productCommissionId: agencyData?.productCommissionId,
          agencyType: agencyData?.agencyType,
          commissionAmt: Number(data.commissionAmt),
          associateType: agencyData?.associateType,
          commissionChangeForAllFutureInvoices: applyFutureRateChange,
          originalAgencyId,
        };
        onAddInvSpecificCommission(invSpecificCommissionData);
        handleClose();
      } else {
        if (onAddInvSpecificCommission) {
          const invSpecificCommissionData = {
            agencyId: selectedAgency.agencyId,
            invoiceAgencyId: agencyData?.invoiceAgencyId,
            agencyName: selectedAgency.agencyName,
            commissionRate: Number(data.commissionRate),
            productCommissionId: agencyData?.productCommissionId,
            agencyType: agencyData?.agencyType,
            commissionAmt: Number(data.commissionAmt),
            associateType: agencyData?.associateType,
            commissionChangeForAllFutureInvoices: applyFutureRateChange,
            originalAgencyId,
          };
          onAddInvSpecificCommission(invSpecificCommissionData);
        }
        handleClose();
      }
    },
    [
      agencyData,
      agencyOptions,
      commissionChangeForAllFutureInvoices,
      isAgencyChanged,
      originalAgencyId,
      onAddInvSpecificCommission,
      dispatch,
      handleClose,
    ]
  );

  const handleConfirmEdit = useCallback(() => {
    if (!pendingFormDataRef.current || !paymentData || !onCommissionRateUpdate) return;

    const newRate = parseFloat(Number(pendingFormDataRef.current.commissionRate).toFixed(2));
    const currentRate = parseFloat(Number(paymentData.currentRate).toFixed(2));

    if (newRate === currentRate) {
      onCommissionRateUpdate({ noChange: true });
      setShowOptionsPopup({ message: "", show: false });
      pendingFormDataRef.current = null;
      return;
    }

    const editCommissionData = {
      invoiceAgencyId: paymentData.invoiceAgencyId,
      paymentId: paymentData.paymentId,
      newCommissionRate: newRate,
      reason: pendingFormDataRef.current.reason || "",
    };

    onCommissionRateUpdate(editCommissionData);
    setShowOptionsPopup({ message: "", show: false });
    pendingFormDataRef.current = null;
    handleClose();
  }, [paymentData, onCommissionRateUpdate, handleClose]);

  const handleCancelConfirmation = useCallback(() => {
    setShowOptionsPopup({ message: "", show: false });
    pendingFormDataRef.current = null;
  }, []);

  const revertAgencySelection = useCallback(() => {
    const confirmed = confirmedAgencyRef.current;
    if (confirmed?.agencyId != null) {
      setValue("agencyId", confirmed.agencyId);
    }
  }, [setValue]);

  const handleCancelAgencyChange = useCallback(() => {
    revertAgencySelection();
    pendingAgencyRef.current = null;
    setShowOptionsPopup({ message: "", show: false });
  }, [revertAgencySelection]);

  const handleConfirmAgencyChange = useCallback(() => {
    const pending = pendingAgencyRef.current;
    if (pending?.agencyId != null) {
      setValue("agencyId", pending.agencyId);
      confirmedAgencyRef.current = pending;
      clearFutureInvoiceRateChange();
    }
    pendingAgencyRef.current = null;
    setShowOptionsPopup({ message: "", show: false });
  }, [setValue, clearFutureInvoiceRateChange]);

  const handleAgencySelect = useCallback(
    (option: any, value: any) => {
      const newAgencyId = option?.value ?? value;
      const newAgencyName =
        option?.label ||
        agencyOptions.find((item) => Number(item.value) === Number(newAgencyId))?.label ||
        "";
      const confirmedId = confirmedAgencyRef.current?.agencyId ?? agencyData?.agencyId;
      if (newAgencyId == null || newAgencyId === "") {
        return;
      }
      if (Number(newAgencyId) === Number(confirmedId)) {
        setValue("agencyId", Number(newAgencyId));
        return;
      }

      revertAgencySelection();
      pendingAgencyRef.current = {
        agencyId: Number(newAgencyId),
        agencyName: newAgencyName,
      };
      const fromAgencyName = confirmedAgencyRef.current?.agencyName || agencyData?.agencyName;
      setShowOptionsPopup({
        message: (
          <>
            You are changing the agency from{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>
              {fromAgencyName}
            </Box>{" "}
            to{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>
              {newAgencyName}
            </Box>
            . Do you wish to proceed?
          </>
        ),
        show: true,
        variant: "warning",
        buttons: [
          { name: "Yes", color: "primary", onClick: handleConfirmAgencyChange },
          { name: "No", color: "error", onClick: handleCancelAgencyChange },
        ],
      });
    },
    [
      agencyOptions,
      agencyData?.agencyId,
      agencyData?.agencyName,
      revertAgencySelection,
      handleConfirmAgencyChange,
      handleCancelAgencyChange,
      setValue,
    ]
  );

  const onSubmit = useCallback(
    async (data: any) => {
      if (mode === "commission-details") {
        pendingFormDataRef.current = data;
        setShowOptionsPopup({
          message: "Do you really want to edit the service rate?",
          show: true,
          variant: "confirm",
          buttons: [
            { name: "Yes", color: "primary", onClick: handleConfirmEdit },
            { name: "No", color: "error", onClick: handleCancelConfirmation },
          ],
        });
        return;
      }

      applyBillingUpdate(data);
    },
    [mode, applyBillingUpdate, handleConfirmEdit, handleCancelConfirmation]
  );

  const handleDialogClose = () => {
    reset();
    setCommissionChangeForAllFutureInvoices(false);
    handleClose();
  };

  const handleCalulateCommissionAmount = () => {
    const rate = getValues("commissionRate");
    const invoiceAmount = getValues("invoiceAmt");
    const commissionAmount = calculateCommissionAmount(Number(invoiceAmount), Number(rate));
    setValue("commissionAmt", commissionAmount, { shouldValidate: true });
  };

  const handleCalculateCommissionRate = () => {
    const commissionAmount = getValues("commissionAmt");
    const invoiceAmount = getValues("invoiceAmt");
    const commissionRate = calculateCommissionRate(Number(invoiceAmount), Number(commissionAmount));
    setValue("commissionRate", commissionRate, { shouldValidate: true });
  };

  const isCommissionDetails = fieldsVariant === "commission-details";
  const title = isCommissionDetails ? "Edit Service Rate" : "Update Service Fee";
  const subtitle = isCommissionDetails
    ? "Adjust the rate for this payment and provide a reason for the change."
    : "Review invoice totals, update the service rate or amount, and optionally apply the rate to future invoices.";
  const submitLabel = isCommissionDetails ? "Save Changes" : "Update Service Fee";
  const ariaDescId = "edit-commission-dialog-description";

  return (
    <>
      <Dialog
        open={open}
        keepMounted
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        sx={zIndex !== undefined ? { zIndex } : undefined}
        PaperProps={{ sx: paperSx(isMobile) }}
        onClick={(e) => e.stopPropagation()}
        aria-describedby={ariaDescId}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={headerSx}>
            <Box sx={iconBoxSx(tokens.accent)}>
              <Percent size={20} color={tokens.accent} strokeWidth={1.8} />
            </Box>
            <Box sx={{ flex: 1, pr: 4 }}>
              <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                {title}
              </Typography>
              <Typography id={ariaDescId} sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                {subtitle}
              </Typography>
            </Box>
            <IconButton onClick={handleDialogClose} size="small" sx={closeIconSx} aria-label="Close">
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              ...bodySx,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
            }}
          >
            <Grid container spacing={2}>
              {agencyData?.invoiceNumber ? (
                <Grid item xs={12} sm={6}>
                  <Box>
                    <FieldLabel label="Invoice #" tokens={tokens} />
                    <Box sx={inputFieldSx()}>
                      <CustomTextField
                        control={control}
                        controllerName="invoiceNumber"
                        label=""
                        size="small"
                        disabled
                      />
                    </Box>
                  </Box>
                </Grid>
              ) : null}
              {agencyData?.orderNumber ? (
                <Grid item xs={12} sm={6}>
                  <Box>
                    <FieldLabel label="Order #" tokens={tokens} />
                    <Box sx={inputFieldSx()}>
                      <CustomTextField
                        control={control}
                        controllerName="orderNumber"
                        label=""
                        size="small"
                        disabled
                      />
                    </Box>
                  </Box>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Box>
                  <FieldLabel label="Agency" tokens={tokens} />
                  {canEditAgency ? (
                    <Box sx={dropdownWrapSx(!!errors.agencyId)}>
                      <CustomDropdownField
                        name="agencyId"
                        control={control}
                        options={agencyOptions}
                        label=""
                        size="small"
                        rules={{ required: "Agency is required." }}
                        onChangeSelect={handleAgencySelect}
                      />
                    </Box>
                  ) : (
                    <Box sx={inputFieldSx(!!errors.agencyName)}>
                      <CustomTextField
                        control={control}
                        controllerName="agencyName"
                        label=""
                        size="small"
                        disabled
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <FieldLabel label="Invoice amount" tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.invoiceAmt)}>
                    <CustomCurrencyField
                      id="invoiceAmt"
                      controllerName="invoiceAmt"
                      control={control}
                      label=""
                      size="small"
                      disabled
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <FieldLabel label="Rate (%)" required tokens={tokens} />
                  <Box sx={inputFieldSx(!!errors.commissionRate)}>
                    <CustomTextField
                      control={control}
                      controllerName="commissionRate"
                      label=""
                      size="small"
                      disabled={disableRateField}
                      rules={{
                        required: "Service Rate is required.",
                        pattern: {
                          value: /^[0-9]+(\.[0-9]+)?$/,
                          message: "Invalid Service Rate.",
                        },
                        validate: (value: any) =>
                          parseFloat(value) <= 100 || "Service rate cannot be more than 100.",
                      }}
                      onBlur={shouldShowAmount && !disableRateField ? handleCalulateCommissionAmount : undefined}
                    />
                  </Box>
                </Box>
              </Grid>
              {shouldShowAmount && (
                <Grid item xs={12}>
                  <Box>
                    <FieldLabel label="Amount ($)" required={isV3CommissionType(allCodeTablesValuesMap, agencyData?.agencyType)} tokens={tokens} />
                    <Box sx={inputFieldSx(!!errors.commissionAmt)}>
                      <CustomCurrencyField
                        id="commissionAmt"
                        controllerName="commissionAmt"
                        control={control}
                        label=""
                        size="small"
                        disabled={disableAmountField}
                        onBlur={!disableAmountField ? handleCalculateCommissionRate : undefined}
                        rules={
                          isV3CommissionType(allCodeTablesValuesMap, agencyData?.agencyType)
                            ? { required: "V3 Revenue Amount is required." }
                            : {}
                        }
                      />
                    </Box>
                  </Box>
                </Grid>
              )}
              {shouldShowReason && (
                <Grid item xs={12}>
                  <Box>
                    <FieldLabel label="Reason" required tokens={tokens} />
                    <Box sx={inputFieldSx(!!errors.reason)}>
                      <CustomTextField
                        control={control}
                        controllerName="reason"
                        label=""
                        size="small"
                        multiline
                        rows={3}
                        rules={{
                          maxLength: {
                            value: 500,
                            message: "Reason cannot exceed 500 characters.",
                          },
                          required: "Reason is required.",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.5,
                        display: "block",
                        color: reasonValue?.length > 500 ? tokens.error : tokens.textSecondary,
                      }}
                    >
                      {reasonValue?.length || 0}/500 characters
                    </Typography>
                  </Box>
                </Grid>
              )}
              {mode === "billing" && !isAgencyChanged && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: { xs: "block", md: "flex" },
                    alignItems: { md: "center" },
                    gap: { md: 1 },
                  }}
                >
                  <CustomCheckbox
                    control={control}
                    label={true}
                    name="commissionChangeForAllFutureInvoices"
                    id="commissionChangeForAllFutureInvoices"
                    controllerName="commissionChangeForAllFutureInvoices"
                    checked={commissionChangeForAllFutureInvoices}
                    onChange={() => handleClick()}
                  />
                  <Typography sx={{ color: tokens.textPrimary, fontSize: "0.9rem", mt: { xs: 0.5, md: 0 } }}>
                    Change this % for all my future invoice for {selectedAgencyName}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          <Box sx={footerSx}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                component="button"
                type="submit"
                sx={{
                  ...primaryBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                }}
              >
                {isCommissionDetails ? (
                  <SaveOutlined sx={{ fontSize: "1.1rem" }} />
                ) : (
                  <EditOutlined sx={{ fontSize: "1.1rem" }} />
                )}
                {submitLabel}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={handleDialogClose}
                sx={{
                  ...cancelBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "1px solid",
                  cursor: "pointer",
                }}
              >
                Cancel
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <OptionsPopup
        open={showOptionsPopup.show}
        onClose={() => {
          setShowOptionsPopup({ message: "", show: false });
        }}
        variant={showOptionsPopup.variant}
        message={showOptionsPopup.message}
        buttons={showOptionsPopup.buttons}
      />
    </>
  );
};

export default EditCommissionPopup;
